import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from './utils/axiosConfig';

const RoleSelection = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // 從 URL 獲取 token 並獲取角色列表
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      // 從 URL 參數獲取 token
      const token = searchParams.get('token');
      
      if (!token) {
        setError('缺少角色選擇 token，請重新登入');
        setLoading(false);
        return;
      }
      
      // 使用 token 獲取角色列表
      const response = await axios.get(`/roles?token=${token}`);
      if (response.data.roles) {
        setRoles(response.data.roles);
      }
    } catch (err) {
      console.error('獲取角色列表失敗:', err);
      const message = err.response?.data?.message || '無法獲取角色列表，請重新登入';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = async (employeeId) => {
    try {
      setSelectedRole(employeeId);
      const roleToken = searchParams.get('token');
      const response = await axios.post('/select-role', {
        employeeId: employeeId,
        roleToken: roleToken  // 傳遞 roleToken 以便後端清除緩存
      });

      if (response.data.success) {
        // 登入成功，跳轉到主頁面
        localStorage.setItem('token', response.data.token);
        window.location.href = response.data.redirectUrl;
      } else {
        setError('角色選擇失敗，請重試');
      }
    } catch (err) {
      setError('角色選擇失敗，請重試');
      setSelectedRole(null);
    }
  };

  const getStoreName = (role) => {
    // 優先使用後端返回的社團名稱
    if (role.store_name) {
      return role.store_name;
    }
    // 備用的靜態映射表
    const storeNames = {
      1: '總會',
      2: '台北分會',
      3: '台中分會',
      4: '高雄分會',
      // 可以根據實際情況添加更多社團
    };
    return storeNames[role.e_ssid] || `社團 ${role.e_ssid}`;
  };

  const getPositionName = (role) => {
    // 優先使用後端返回的社職名稱
    if (role.position_name) {
      return role.position_name;
    }
    // 備用顯示
    return `職位 ${role.e_position}`;
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">載入中...</span>
              </div>
              <p className="mt-3">正在載入您的角色資訊...</p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card>
            <Card.Body>
              <h2 className="text-center mb-4">
                <i className="fas fa-user-friends me-2"></i>
                選擇登入角色
              </h2>
              
              {error && (
                <Alert variant="danger" className="text-center">
                  {error}
                </Alert>
              )}

              <Alert variant="info" className="text-center">
                <i className="fas fa-info-circle me-2"></i>
                檢測到您在多個社團擁有不同身份，請選擇要登入的角色
              </Alert>

              <Row>
                {roles.map((role, index) => (
                  <Col md={6} key={role.e_id} className="mb-3">
                    <Card 
                      className={`h-100 role-card ${selectedRole === role.e_id ? 'border-primary' : ''}`}
                      style={{ 
                        cursor: selectedRole === role.e_id ? 'not-allowed' : 'pointer',
                        opacity: selectedRole && selectedRole !== role.e_id ? 0.6 : 1
                      }}
                    >
                      <Card.Body className="text-center">
                        <div className="mb-3">
                          <i className="fas fa-building fa-2x text-primary"></i>
                        </div>
                        <h5 className="card-title">{getStoreName(role)}</h5>
                        <p className="card-text">
                          <strong>姓名：</strong>{role.e_name}<br/>
                          <strong>社職：</strong>{getPositionName(role)}<br/>
                          <strong>電話：</strong>{role.e_mobile}
                        </p>
                        <Button 
                          variant={selectedRole === role.e_id ? 'secondary' : 'primary'}
                          className="w-100"
                          onClick={() => handleRoleSelect(role.e_id)}
                          disabled={selectedRole !== null}
                        >
                          {selectedRole === role.e_id ? (
                            <>
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">處理中...</span>
                              </div>
                              登入中...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-sign-in-alt me-2"></i>
                              選擇此身份登入
                            </>
                          )}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <hr className="my-4" />
              
              <div className="text-center">
                <Button 
                  variant="link" 
                  onClick={() => window.location.href = '/login'}
                  disabled={selectedRole !== null}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  返回登入頁面
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RoleSelection;